/**
 * AppliedChips Component
 *
 * Displays applied formation and concept references as removable chips.
 * Used in the play editor to show which formation and concepts are applied.
 */

import { X } from 'lucide-react'
import type { FormationBlock, ConceptBlock, ConceptReferenceBlock } from '@/types/blocks'
import './applied-chips.css'

interface AppliedChipsProps {
	/** The formation block currently applied (null if none) */
	formationBlock: FormationBlock | null
	/** Array of concept references with their resolved concept blocks */
	conceptReferences: Array<{
		reference: ConceptReferenceBlock
		concept: ConceptBlock
	}>
	/** Called when user removes the formation */
	onRemoveFormation: () => void
	/** Called when user removes a concept reference */
	onRemoveConcept: (referenceId: string) => void
}

/**
 * Displays applied formation and concept chips with remove buttons.
 */
export function AppliedChips({
	formationBlock,
	conceptReferences,
	onRemoveFormation,
	onRemoveConcept,
}: AppliedChipsProps) {
	const hasContent = formationBlock !== null || conceptReferences.length > 0

	if (!hasContent) {
		return <div data-testid="applied-chips-container" />
	}

	return (
		<div className="applied-chips" data-testid="applied-chips-container">
			{formationBlock && (
				<div
					className="applied-chip"
					data-testid="chip-formation"
					data-variant="formation"
				>
					<span className="applied-chip__label">{formationBlock.properties.name}</span>
					<button
						onClick={onRemoveFormation}
						className="applied-chip__remove"
						aria-label={`Remove ${formationBlock.properties.name}`}
						type="button"
					>
						<X className="applied-chip__remove-icon" />
					</button>
				</div>
			)}

			{conceptReferences.map(({ reference, concept }) => (
				<div
					key={reference.id}
					className="applied-chip"
					data-testid={`chip-concept-${reference.id}`}
					data-variant="concept"
				>
					<span className="applied-chip__label">{concept.properties.name}</span>
					<button
						onClick={() => onRemoveConcept(String(reference.id))}
						className="applied-chip__remove"
						aria-label={`Remove ${concept.properties.name}`}
						type="button"
					>
						<X className="applied-chip__remove-icon" />
					</button>
				</div>
			))}
		</div>
	)
}
